'use client';

import '@assistant-ui/react-markdown/styles/dot.css';

import { memo, useCallback, useMemo } from 'react';

import { useAuiState, useSmooth } from '@assistant-ui/react';
import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from '@assistant-ui/react-markdown';
import remarkGfm from 'remark-gfm';

import { CodeHeader } from '@/components/ui/code-header';
import { MarkdownAnchor } from '@/components/ui/markdown-anchor';
import { MarkdownImage } from '@/components/ui/markdown-image';
import { MarkdownImageGallery } from '@/components/ui/markdown-image-gallery';
import { rehypeImageGallery } from '@/lib/plugins/rehype-image-gallery';
import { cn } from '@/lib/utils';
import { sanitizeStreamingMarkdown } from '@/lib/utils/markdown-sanitizer';
import { markdownUrlTransform } from '@/lib/utils/markdown-url';

function MarkdownTextImpl() {
  // MarkdownText only renders inside a text/reasoning part; throw otherwise to
  // keep the strict behavior of the removed useMessagePartText hook.
  const messagePart = useAuiState(({ part }) => {
    if (part.type !== 'text' && part.type !== 'reasoning') {
      throw new Error('MarkdownText must render inside a text or reasoning message part');
    }
    return part;
  });
  const { text: smoothText } = useSmooth(messagePart, true);
  const sanitizedText = useMemo(() => sanitizeStreamingMarkdown(smoothText), [smoothText]);
  const preprocess = useCallback(() => sanitizedText, [sanitizedText]);

  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeImageGallery]}
      // Keep tel:/mailto:/sms: intact; react-markdown's default transform blanks them.
      urlTransform={markdownUrlTransform}
      className="aui-md"
      components={markdownComponents}
      smooth={false}
      preprocess={preprocess}
    />
  );
}

export const MarkdownText = memo(MarkdownTextImpl);

const defaultComponents: ReturnType<typeof memoizeMarkdownComponents> = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        'aui-md-h1 mb-8 scroll-m-20 text-4xl font-extrabold tracking-tight last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        'aui-md-h2 mt-8 mb-4 scroll-m-20 text-3xl font-semibold tracking-tight first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        'aui-md-h3 mt-6 mb-4 scroll-m-20 text-2xl font-semibold tracking-tight first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        'aui-md-h4 mt-6 mb-4 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0 last:mb-0',
        className,
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn('aui-md-h5 my-4 text-lg font-semibold first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6 className={cn('aui-md-h6 my-4 font-semibold first:mt-0 last:mb-0', className)} {...props} />
  ),
  p: ({ className, ...props }) => (
    <p className={cn('aui-md-p mt-5 mb-5 leading-7 first:mt-0 last:mb-0', className)} {...props} />
  ),
  a: MarkdownAnchor,
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn('aui-md-blockquote border-s-2 border-border ps-6 italic', className)}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul className={cn('aui-md-ul my-5 ms-6 list-disc [&>li]:mt-2', className)} {...props} />
  ),
  ol: ({ className, ...props }) => (
    <ol className={cn('aui-md-ol my-5 ms-6 list-decimal [&>li]:mt-2', className)} {...props} />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn('aui-md-hr my-5 border-b border-border', className)} {...props} />
  ),
  table: ({ className, ...props }) => (
    <div className="my-5 w-full overflow-y-auto">
      <table
        className={cn('aui-md-table w-full border-separate border-spacing-0', className)}
        {...props}
      />
    </div>
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        'aui-md-th bg-muted px-4 py-2 text-start font-bold first:rounded-tl-lg last:rounded-tr-lg rtl:first:rounded-tl-none rtl:first:rounded-tr-lg rtl:last:rounded-tl-lg rtl:last:rounded-tr-none [&[align=center]]:text-center [&[align=right]]:text-end',
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        'aui-md-td border-s border-b border-border px-4 py-2 text-start last:border-e [&[align=center]]:text-center [&[align=right]]:text-end',
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        'aui-md-tr m-0 border-b border-border p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg rtl:[&:last-child>td:first-child]:rounded-br-lg rtl:[&:last-child>td:first-child]:rounded-bl-none [&:last-child>td:last-child]:rounded-br-lg rtl:[&:last-child>td:last-child]:rounded-br-none rtl:[&:last-child>td:last-child]:rounded-bl-lg',
        className,
      )}
      {...props}
    />
  ),
  sup: ({ className, ...props }) => (
    <sup className={cn('aui-md-sup [&>a]:text-xs [&>a]:no-underline', className)} {...props} />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        'aui-md-pre overflow-x-auto !rounded-t-none rounded-b-lg bg-black p-4 text-white',
        className,
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          !isCodeBlock && 'aui-md-inline-code rounded border bg-muted font-semibold',
          className,
        )}
        {...props}
      />
    );
  },
  CodeHeader,
});

export const markdownComponents = {
  ...defaultComponents,
  'image-gallery': MarkdownImageGallery,
  img: MarkdownImage,
} as unknown as typeof defaultComponents;
